import { useEffect, useState } from 'react'
import { useRouteError } from 'react-router-dom'
import { defineMessages, useT } from '../i18n/index.ts'
import { useScreen } from '../lib/screens.ts'
import { reportProblem } from '../lib/telemetry.ts'
import { Button } from '../ui/Button.tsx'
import { IconAlert } from '../ui/Icon.tsx'
import { Wordmark } from '../ui/Logo.tsx'
import { Spinner } from '../ui/Spinner.tsx'
import { EmptyState } from '../ui/Surface.tsx'
import { isChunkLoadError } from './chunkRecovery.ts'
import { recover, retryRoute } from './staleBuild.ts'

const messages = defineMessages({
	en: {
		title: 'This page could not load',
		body: 'Temaly was updated while this tab was open, or the connection dropped. Reload to carry on; your saved work is safe.',
		reload: 'Reload',
	},
	ro: {
		title: 'Pagina nu s-a putut încărca',
		body: 'Temaly a fost actualizat cât timp fila era deschisă, sau conexiunea s-a întrerupt. Reîncarcă pentru a continua; lucrul salvat este în siguranță.',
		reload: 'Reîncarcă',
	},
	ru: {
		title: 'Не удалось загрузить страницу',
		body: 'Temaly обновился, пока вкладка была открыта, или пропало соединение. Обновите страницу, чтобы продолжить; сохранённая работа в безопасности.',
		reload: 'Обновить',
	},
	fr: {
		title: 'Cette page n’a pas pu se charger',
		body: 'Temaly a été mis à jour pendant que cet onglet était ouvert, ou la connexion a été coupée. Rechargez pour continuer ; votre travail enregistré est en sécurité.',
		reload: 'Recharger',
	},
	it: {
		title: 'Questa pagina non si è caricata',
		body: 'Temaly è stato aggiornato mentre questa scheda era aperta, oppure la connessione è caduta. Ricarica per continuare; il lavoro salvato è al sicuro.',
		reload: 'Ricarica',
	},
	kk: {
		title: 'Бет жүктелмеді',
		body: 'Қойынды ашық тұрғанда Temaly жаңартылды немесе байланыс үзілді. Жалғастыру үшін бетті қайта жүктеңіз; сақталған жұмысыңыз қауіпсіз.',
		reload: 'Қайта жүктеу',
	},
	es: {
		title: 'No se pudo cargar esta página',
		body: 'Temaly se actualizó mientras esta pestaña estaba abierta, o se cortó la conexión. Recarga para continuar; tu trabajo guardado está a salvo.',
		reload: 'Recargar',
	},
	pt: {
		title: 'Não foi possível carregar esta página',
		body: 'O Temaly foi atualizado enquanto esta aba estava aberta, ou a conexão caiu. Recarregue para continuar; o seu trabalho salvo está seguro.',
		reload: 'Recarregar',
	},
	de: {
		title: 'Diese Seite konnte nicht geladen werden',
		body: 'Temaly wurde aktualisiert, während dieser Tab offen war, oder die Verbindung ist abgebrochen. Lade neu, um weiterzumachen; deine gespeicherte Arbeit ist sicher.',
		reload: 'Neu laden',
	},
})

/**
 * What a route shows when it throws or its chunk will not import. A chunk failure reloads once on its
 * own, after saving her edits (staleBuild.ts), whether a deploy or a dropped request caused it; anything
 * else, or a second failure, shows the message and a Reload button. `bare` is for the public pages, which carry no app shell: it
 * brings its own mark. Inside the app shell the header around it stays.
 */
export function RouteError({ bare = false }: { bare?: boolean }) {
	const t = useT(messages)
	const error = useRouteError()
	// Inside the shell she is a teacher with a session; the bare one also catches student links.
	useScreen('error', { enabled: !bare })
	const chunk = isChunkLoadError(error)
	// A chunk failure shows the spinner while the one reload is on its way; anything else, or a
	// second failure, is shown at once.
	const [gaveUp, setGaveUp] = useState(!chunk)

	useEffect(() => {
		let live = true
		const giveUp = () => {
			reportProblem(
				`route: ${error instanceof Error ? error.message : String(error)}`,
				error instanceof Error ? error.stack : undefined,
			)
			if (live) setGaveUp(true)
		}
		if (chunk)
			void recover().then(async (r) => {
				if (r === 'reloaded') return
				// No deploy: the import broke on the way (a phone dropping or cancelling requests). Once more.
				if (r === 'same' && (await retryRoute())) return
				giveUp()
			})
		else giveUp()
		return () => {
			live = false
		}
	}, [chunk, error])

	const body = !gaveUp ? (
		<div role="status" className="flex justify-center py-16 text-teal-700">
			<Spinner className="size-6" />
		</div>
	) : (
		<EmptyState
			icon={<IconAlert />}
			title={t.title}
			action={
				<Button id="route-error-reload" variant="primary" onClick={() => location.reload()}>
					{t.reload}
				</Button>
			}
		>
			{t.body}
		</EmptyState>
	)
	if (!bare) return body
	return (
		<div className="flex min-h-dvh flex-col bg-canvas">
			<header className="mx-auto flex h-15 w-full max-w-300 items-center px-4 sm:px-6 lg:px-10">
				<Wordmark />
			</header>
			<main className="mx-auto w-full max-w-300 flex-1 px-4 pt-10 sm:px-6 lg:px-10">{body}</main>
		</div>
	)
}
